import React from 'react';
import I18n from 'i18n-js';
import { gql } from '@reverbdotcom/commons/src/gql';
import { useQuery } from '@reverbdotcom/commons/src/useQuery';
import { ShopBadges } from '@reverbdotcom/commons/src/components/shop_badges';
import CoreLink from '@reverbdotcom/commons/src/components/core_link';
import { SHOP_FEEDBACK_PATH, SHOP_PATH } from '@reverbdotcom/commons/src/url_helpers';
import URLHelpers, { adminEditUserPath } from './shared/url_helpers';
import ShopPoliciesModal from './item2_shop_policies_modal';
import { getYear } from '../lib/date_utils';
import ShopSvg from '@reverbdotcom/cadence/images/brand/my-shop-orange.svg';
import { MParticleEventName } from '@reverbdotcom/commons/src/elog/mparticle_types';
import { RCAvatar, RCGrid, RCStarRating, RCTextWithIcon, useMediaQuery } from '@reverbdotcom/cadence/components';
import { useUser } from '@reverbdotcom/commons/src/user_hooks';
import FavoriteShopCTA from './favorites/favorite_shop_cta';
import { ChevronRightIcon } from '@reverbdotcom/cadence/icons/react';
import {
  BumpKeyFragment,
  Core_About_This_Seller_Find_FavoriteQuery,
  Core_About_This_Seller_Find_FavoriteQueryVariables,
  Item2AboutThisSellerDataFragment,
} from '@reverbdotcom/commons/src/gql/graphql';

interface IProps {
  listing: Item2AboutThisSellerDataFragment & BumpKeyFragment;
}

export const Item2AboutThisSellerFragment = gql(`
  fragment Item2AboutThisSellerData on Listing {
    _id
    id
    seller {
      _id
      id
      uuid
      avatar {
        source
      }
      feedbackSummary {
        receivedCount
        rollingRatingPercentage
      }
    }
    shop {
      _id
      name
      description
      slug
      preferredSeller
      quickShipper
      quickResponder
      address {
        _id
        displayLocation
        countryCode
      }
      createdAt
      orders {
        total
      }
    }
  }
`);

export function componentClickEvent(displayStyle, path) {
  return {
    eventName: MParticleEventName.ClickedComponent,
    componentName: 'Item2AboutThisSeller',
    displayStyle,
    entityType: 'url',
    entityId: path,
  };
}

function Avatar({ shopURL, shopName, avatarURL }) {
  return (
    <RCGrid.Item
      key="shop-avatar"
      colSpan={{ desktop: 2, tablet: 3, mobile: 4 }}
      order={{ desktop: 1, mobile: 2 }}
    >
      <div className="item2-about-this-seller__avatar">
        <CoreLink
          to={shopURL}
          clickEvent={componentClickEvent('imageLink', shopURL)}
        >
          <RCAvatar
            userName={shopName}
            thumbnailURL={avatarURL || ShopSvg}
            thumbnailFallback="initial"
          />
        </CoreLink>
      </div>
    </RCGrid.Item>
  );
}

function ShopInfo({ shopURL, shop, user, seller, isMobile }) {
  const { preferredSeller, quickResponder, quickShipper } = shop;

  return (
    <RCGrid.Item key="shop-info"
      colSpan={{ desktop: 6, tablet: 5, mobile: 8 }}
      order={{ desktop: 2, mobile: 1 }}
    >
      <div className="d-flex fx-dir-col gap-4">
        <div className="d-flex fx-dir-col gap-2">
          <h4>
            <CoreLink
              to={shopURL}
              className="item2-about-this-seller__title"
              clickEvent={componentClickEvent('titleLink', shopURL)}
            >
              {shop.name}
            </CoreLink>
            {user.isAdmin &&
              <CoreLink
                to={adminEditUserPath.expand({ id: seller.id })}
                className="ml-1 size-140 color-accent"
                data-user-edit
              >
                <span className="fa fa-magic" />
              </CoreLink>
            }
          </h4>
          <div className="item2-about-this-seller__location-and-rating">
            <span>{shop.address?.displayLocation}</span>

            <RCStarRating
              rating={seller.feedbackSummary.rollingRatingPercentage / 100}
              count={seller.feedbackSummary.receivedCount}
            />
          </div>
        </div>

        <div className="item2-about-this-seller__stats">
          <div>
            <span className="mr-space">
              {I18n.t('discovery.item2Shop.joined')}:
            </span>
            <span className="item2-about-this-seller__quantity">{getYear(shop.createdAt)}</span>
          </div>
          {shop.orders?.total > 0 &&
            <div>
              <span className="mr-space">
                {I18n.t('discovery.item2AboutThisSeller.itemsSold')}:
              </span>
              <span className="item2-about-this-seller__quantity">{shop.orders?.total.toLocaleString()}</span>
            </div>
          }
        </div>

        <ShopBadges
          preferredSeller={preferredSeller}
          quickShipper={quickShipper}
          quickResponder={quickResponder}
          hideLabels={isMobile}
        />
      </div>
    </RCGrid.Item>
  );
}

function ShopActions({ shop, initialFavoriteId, listing, seller, shopURL }) {
  const shopFeedbackURL = SHOP_FEEDBACK_PATH.expand({ slug: shop.slug });
  const messageURL = URLHelpers.newDashboardMessagesPath(
    listing.id,
    seller?.uuid,
    listing.bumpKey?.key,
  );

  return (
    <RCGrid.Item key="shop-actions"
      colSpan={{ desktop: 4, tablet: 4, mobile: 12 }}
      order={{ desktop: 3 }}
    >
      <div className="item2-about-this-seller__actions">
        <FavoriteShopCTA
          shop={shop}
          initialFavoriteId={initialFavoriteId}
          theme="outlined"
          parentComponentName= "Item2AboutThisSeller"
        />

        <ShopPoliciesModal
          asButtonStyledAsLink
          listingId={listing.id}
          clickEvent={{
            eventName: MParticleEventName.ClickedI2ShopPoliciesModal,
            componentName: 'Item2AboutThisSeller',
            listingId: listing.id,
          }}
        />

        <CoreLink
          to={messageURL}
          className="button-as-link color-black weight-bold"
          data-registration="required"
          data-send-message
          clickEvent={componentClickEvent('messageSellerLink', messageURL)}
        >
          <RCTextWithIcon svgComponent={ChevronRightIcon}>
            {I18n.t('discovery.item2Shop.messageSeller')}
          </RCTextWithIcon>
        </CoreLink>

        <CoreLink
          to={shopFeedbackURL}
          className="button-as-link color-black weight-bold"
          data-registration="required"
        >
          <RCTextWithIcon svgComponent={ChevronRightIcon}>
            {I18n.t('discovery.item2Layout.sellerReviews')}
          </RCTextWithIcon>
        </CoreLink>

        <CoreLink
          to={shopURL}
          className="button-as-link color-black weight-bold"
          clickEvent={componentClickEvent('viewShopLink', shopURL)}
        >
          <RCTextWithIcon svgComponent={ChevronRightIcon}>
            {I18n.t('discovery.item2AboutThisSeller.viewShop')}
          </RCTextWithIcon>
        </CoreLink>
      </div>
    </RCGrid.Item>
  );
}

export function Item2AboutThisSeller({ listing }: IProps) {
  const { shop, seller } = listing;
  const user = useUser();
  const isMobile = useMediaQuery('mobile');
  const { data } = useQuery<Core_About_This_Seller_Find_FavoriteQuery, Core_About_This_Seller_Find_FavoriteQueryVariables>(ABOUT_THIS_SELLER_FAVORITES_QUERY, {
    ssr: true,
    variables: {
      shopSlug: shop?.slug,
      loggedOut: user.loggedOut,
    },
  });

  if (!shop) return null;

  const shopURL = SHOP_PATH.expand({ slug: shop.slug });
  const avatarUrl = seller?.avatar?.source;
  const initialFavoriteId = data?.findFavorite?.favorite?.id;

  return (
    <div className="item2-about-this-seller">
      <h2 className="item2-about-this-seller__heading">
        {I18n.t('discovery.item2ShopModule.aboutTheSeller')}
      </h2>
      <RCGrid>
        <ShopInfo
          shopURL={shopURL}
          shop={shop}
          isMobile={isMobile}
          user={user}
          seller={seller}
        />
        <Avatar
          shopName={shop.name}
          shopURL={shopURL}
          avatarURL={avatarUrl}
        />
        <ShopActions
          shop={shop}
          initialFavoriteId={initialFavoriteId}
          listing={listing}
          seller={seller}
          shopURL={shopURL}
        />
      </RCGrid>
    </div>
  );
}

export const ABOUT_THIS_SELLER_FAVORITES_QUERY = gql(`
  query Core_About_This_Seller_Find_Favorite(
    $shopSlug: String
    $loggedOut: Boolean!
  ) {
    findFavorite(
      input: {
        shopSlug: $shopSlug
      }
    ) @skip(if: $loggedOut) {
      favorite {
        id
      }
    }
  }
`);
